// src/lib/humanize.js -- UI label helpers.
// Converts internal snake_case/kebab-case ids into human-readable
// Title Case labels. Safe-guards against humanizing user-entered
// names (emails, URLs, UUIDs, free-text strings).
// ASCII only. var only. ESM.

// PD42h-6a: sitewide cleanup. Use labelFor() anywhere a UI label falls
// through to an id. The goal: users never see 'memory_timeline', they
// see 'Memory Timeline'. Actual user-entered names (with underscores
// or not) are left alone.

var UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
var EMAIL_RE = /@/;
var URL_RE = /^https?:\/\//i;

// Is this string a schema-style id we should humanize?
// Heuristics:
//   - short (<= 40 chars)
//   - not an email / url / UUID
//   - contains at least one underscore or hyphen, OR is all lowercase
//     (e.g. 'brief', 'agent_hub', 'memory-timeline')
function _looksLikeInternalId(s) {
  if (!s || typeof s !== 'string') return false;
  if (s.length > 40) return false;
  if (EMAIL_RE.test(s)) return false;
  if (URL_RE.test(s)) return false;
  if (UUID_RE.test(s)) return false;
  if (/[_-]/.test(s)) return true;
  if (s === s.toLowerCase() && /^[a-z0-9]+$/.test(s)) return true;
  return false;
}

// Convert 'memory_timeline' / 'memory-timeline' -> 'Memory Timeline'.
// Non-id-shaped strings pass through unchanged.
export function humanize(s) {
  if (!s || typeof s !== 'string') return s || '';
  if (!_looksLikeInternalId(s)) return s;
  var stripped = s.replace(/^agent_/, '');
  return stripped
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, function(c) { return c.toUpperCase(); });
}

// Primary export. Given an object and a list of preferred fields,
// returns the first non-empty one. If it lands on a field listed in
// `idFields`, humanizes the result. Otherwise returns as-is.
//
//   labelFor(agent)                        -> tries display_name, name, title, id
//   labelFor(obj, ['display_name', 'id'])  -> custom field order; humanize id fallback
//   labelFor(obj, ['name'], [])            -> never humanize
export function labelFor(obj, preferredFields, idFields) {
  if (!obj || typeof obj !== 'object') return '';
  var fields = preferredFields || ['display_name', 'name', 'title', 'id'];
  var ids = idFields || ['id', 'agent_id', 'clone_id', 'tenant_id'];
  for (var i = 0; i < fields.length; i++) {
    var f = fields[i];
    var v = obj[f];
    if (v !== undefined && v !== null && v !== '') {
      if (ids.indexOf(f) >= 0) return humanize(String(v));
      return String(v);
    }
  }
  return '';
}

export default { humanize: humanize, labelFor: labelFor };
